import React from 'react';

type StudioIconProps = {
  readonly children: React.ReactNode;
};

function StudioIcon(props: StudioIconProps): React.ReactElement {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {props.children}
    </svg>
  );
}

export function PaperclipIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </StudioIcon>
  );
}

export function SendIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </StudioIcon>
  );
}

export function StopIcon(): React.ReactElement {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

export function GitMergeIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <circle cx="18" cy="18" r="3" />
      <circle cx="6" cy="6" r="3" />
      <path d="M6 21V9a9 9 0 0 0 9 9" />
    </StudioIcon>
  );
}

export function SyncBaseIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
      <path d="M16 16h5v5" />
    </StudioIcon>
  );
}

export function LinkIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </StudioIcon>
  );
}

export function TrashIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" x2="10" y1="11" y2="17" />
      <line x1="14" x2="14" y1="11" y2="17" />
    </StudioIcon>
  );
}

export function CloseIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </StudioIcon>
  );
}

export function MaximizeIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" x2="14" y1="3" y2="10" />
      <line x1="3" x2="10" y1="21" y2="14" />
    </StudioIcon>
  );
}

export function RestoreIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" x2="21" y1="10" y2="3" />
      <line x1="3" x2="10" y1="21" y2="14" />
    </StudioIcon>
  );
}

export function CollapseChatIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <polyline points="9 18 15 12 9 6" />
    </StudioIcon>
  );
}

export function ExpandChatIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <polyline points="15 18 9 12 15 6" />
    </StudioIcon>
  );
}

export function MicIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" x2="12" y1="19" y2="22" />
    </StudioIcon>
  );
}

export function CrosshairIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <circle cx="12" cy="12" r="7" />
      <line x1="12" x2="12" y1="2" y2="6" />
      <line x1="12" x2="12" y1="18" y2="22" />
      <line x1="2" x2="6" y1="12" y2="12" />
      <line x1="18" x2="22" y1="12" y2="12" />
    </StudioIcon>
  );
}

export function CameraIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </StudioIcon>
  );
}

/** Teach-by-demonstration: show a browser path. */
export function TeachPathIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <circle cx="6" cy="6" r="2" />
      <circle cx="18" cy="18" r="2" />
      <path d="M8 7c4 0 4 10 8 10" />
      <path d="M14 6h4v4" />
    </StudioIcon>
  );
}

export function HistoryIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l4 2" />
    </StudioIcon>
  );
}

export function StarIcon(props: { readonly filled?: boolean }): React.ReactElement {
  if (props.filled === true) {
    return (
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    );
  }
  return (
    <StudioIcon>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </StudioIcon>
  );
}

export function ChevronDownIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="m6 9 6 6 6-6" />
    </StudioIcon>
  );
}

export function MoreVerticalIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
    </StudioIcon>
  );
}

export function LockIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </StudioIcon>
  );
}

export function UnlockIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 7.5-1.8" />
    </StudioIcon>
  );
}

export function ChevronLeftIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="m15 18-6-6 6-6" />
    </StudioIcon>
  );
}

export function ChevronRightIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <path d="m9 18 6-6-6-6" />
    </StudioIcon>
  );
}

export function DesktopIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" x2="16" y1="21" y2="21" />
      <line x1="12" x2="12" y1="17" y2="21" />
    </StudioIcon>
  );
}

export function TabletIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="12" x2="12.01" y1="18" y2="18" />
    </StudioIcon>
  );
}

export function MobileIcon(): React.ReactElement {
  return (
    <StudioIcon>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <line x1="12" x2="12.01" y1="18" y2="18" />
    </StudioIcon>
  );
}
